import { ArrowDownIcon } from '@systemeio/ui/icons'
import { ActionMenu, ActionMenuItem, ContactFiledInterface, DangerButton, FormSelect, Hint, toast } from '@systemeio/ui'
import {
  addTag,
  deleteContacts,
  exportContacts,
  removeTag,
  resubscribeEmail,
  subscribeToCampaign,
  unsubscribeEmail,
  unsubscribeFromCampaign,
} from 'modules/contacts/api/contact-api'
import { UtmParameterEnum } from 'modules/contacts/enums/utm-parameter-enum'
import { useContactCampaigns } from 'modules/contacts/hooks/use-contact-campaigns'
import { useNeedShowUtmParameters } from 'modules/contacts/hooks/use-need-show-utm-parameters'
import { ContactExportInterface } from 'modules/contacts/types/contact-export-interface'
import { ContactFilterConditionInterface } from 'modules/contacts/types/contact-filter-interface'
import { ContactApiInterface } from 'modules/contacts/types/contact-interface'
import {
  ContactBulkData,
  ContactBulkInterface,
  ContactCampaignInterface,
  ContactTagInterface,
} from 'modules/contacts/types/contact-menu-interface'
import { useTagsAll } from 'modules/tags/hooks/use-tags-all'
import React, { Fragment, useEffect, useState } from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import CreateModal from 'shared/components/create-modal'
import CsvExportModal, { ExportOptionsInterface } from 'shared/components/csv-export-modal'
import TransLoco from 'shared/components/trans-loco'
import { AdminRolesEnum } from 'shared/enums/user-roles-enum'
import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import { ListingSelectType } from 'shared/hooks/use-listing-select'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useModal } from 'shared/hooks/use-modal'
import useUser from 'shared/hooks/use-user'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'
import { KeyedMutator } from 'swr'
import { twMerge } from 'tailwind-merge'
import { ExtraColumnDataItemsIdsEnum } from '../constants/contact-extra-columns-items'
import { getExportColumnsData } from '../utils/get-export-columns-data'
import { useCampaignsList } from '../../campaigns/hooks/use-campaigns-list'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'

interface ContactsMenuProps {
  fields?: ContactFiledInterface[]
  conditions: ContactFilterConditionInterface[]
  mutateContacts: KeyedMutator<ContactApiInterface>
  getListingSelect: ListingSelectType
}

interface BulkErrorsInterface {
  tag: string
  campaign: string
  campaignStep: string
}

const defaultTagValues: ContactTagInterface = { tag: undefined }
const defaultCampaignValues: ContactCampaignInterface = {
  campaign: undefined,
  campaignStep: undefined,
}
const defaultErrors: BulkErrorsInterface = { tag: '', campaign: '', campaignStep: '' }

const ContactsMenu = ({
  fields,
  conditions,
  mutateContacts,
  getListingSelect,
}: ContactsMenuProps) => {
  const { t } = useLocoTranslation()

  const [filterData, setFilterData] = useState<ContactBulkInterface>({
    contactIds: [],
    conditions: [],
    type: 'all-filtered-contacts',
  })

  const { renderListingSelect, closeMenu, selectedAll, selectedIds, exceptionIds } =
    getListingSelect(t('dashboard.contacts.selected_contacts'))

  const { isWsAssistant, checkImpersonatorAdminRoles, isSupportImpersonation } = useUser()
  const needShowUtmParameters = useNeedShowUtmParameters()

  const { revalidateModules } = useRevalidateModules()

  useEffect(() => {
    setFilterData({
      contactIds: selectedAll ? Array.from(exceptionIds) : Array.from(selectedIds),
      type: selectedAll ? 'all-filtered-contacts' : 'only-selected-contacts',
      conditions: conditions,
    })
  }, [selectedAll, selectedIds, conditions, exceptionIds])

  const resubscribeEmailModal = useModal(false)
  const [tagAddModalOpened, setTagAddModalOpened] = useState(false)
  const [tagRemoveModalOpened, setTagRemoveModalOpened] = useState(false)
  const [campaignSubscribeModalOpened, setCampaignSubscribeModalOpened] = useState(false)
  const [campaignUnsubscribeModalOpened, setCampaignUnsubscribeModalOpened] = useState(false)
  const [emailUnsubscribeModalOpened, setEmailUnsubscribeModalOpened] = useState(false)
  const [deleteModalOpened, setDeleteModalOpened] = useState(false)
  const [exportModalOpened, setExportModalOpened] = useState(false)
  const [tagData, setTagData] = useState<ContactBulkData[] | undefined>(undefined)

  const { fetcher: postFetcher } = useFetch({
    method: RequestMethodsEnum.post,
  })

  const [errors, setErrors] = useState<BulkErrorsInterface>(defaultErrors)

  const onAddTag = async (newData: ContactTagInterface) => {
    await fetchWithErrors(async () => {
      await addTag({ ...newData, ...filterData })
    }, setErrors)
    await mutateContacts(data => {
      if (data && !selectedAll) {
        const newTag = tagData?.find(el => el.id === newData.tag)
        if (newTag) {
          return {
            ...data,
            items: data.items.map(contact => {
              if (selectedIds.has(contact.id)) {
                if (!contact.tags.map(el => el.id).includes(newTag.id)) {
                  contact = {
                    ...contact,
                    tags: [...contact.tags, { name: newTag.caption, id: newTag.id }],
                  }
                }
              }
              return contact
            }),
          }
        }
      }
    }, selectedAll)

    await revalidateModules([RequestModuleEnum.Contacts])

    toast.success(t('dashboard.contacts.actions.tag_added'))
    closeMenu()
  }
  const onRemoveTag = async (newData: ContactTagInterface) => {
    await fetchWithErrors(async () => {
      await removeTag({ ...newData, ...filterData })
    }, setErrors)
    await mutateContacts(data => {
      if (data && !selectedAll) {
        const removedTag = tagData?.find(el => el.id === newData.tag)
        if (removedTag) {
          return {
            ...data,
            items: data.items.map(contact => {
              if (selectedIds.has(contact.id)) {
                contact = {
                  ...contact,
                  tags: [...contact.tags.filter(el => el.id !== removedTag.id)],
                }
              }
              return contact
            }),
          }
        }
      }
    }, selectedAll)

    await revalidateModules([RequestModuleEnum.Contacts])

    toast.success(t('dashboard.contacts.actions.tag_removed'))
    closeMenu()
  }

  const [campaignData, setCampaignData] = useState<ContactBulkData[] | undefined>(undefined)
  const [campaignStepData, setCampaignStepData] = useState<ContactBulkData[] | undefined>(undefined)
  const onSubscribeToCampaign = async (data: ContactCampaignInterface) => {
    if (!data.campaignStep) {
      delete data.campaignStep
    }
    await fetchWithErrors(async () => {
      await subscribeToCampaign({ ...data, ...filterData })
    }, setErrors)

    await revalidateModules([RequestModuleEnum.Contacts])

    toast.success(t('dashboard.contacts.actions.contacts_subscribed'))
    closeMenu()
  }
  const onUnsubscribeFromCampaign = async (data: ContactCampaignInterface) => {
    delete data.campaignStep
    await fetchWithErrors(async () => {
      await unsubscribeFromCampaign({ ...data, ...filterData })
    }, setErrors)

    await revalidateModules([RequestModuleEnum.Contacts])

    toast.success(t('dashboard.contacts.actions.contacts_unsubscribed'))
    closeMenu()
  }

  const onResubscribeEmail = async () => {
    await resubscribeEmail({ ...filterData })
    await mutateContacts(data => {
      if (data && !selectedAll) {
        return {
          ...data,
          items: data.items.map(contact => {
            if (selectedIds.has(contact.id)) {
              contact = {
                ...contact,
                unsubscribed: !contact.unsubscribed,
              }
            }
            return contact
          }),
        }
      }
    }, selectedAll)

    await revalidateModules([RequestModuleEnum.Contacts])

    toast.success(t('dashboard.contacts.actions.email_resubscribed'))
    closeMenu()
  }
  const onUnsubscribeEmail = async () => {
    await unsubscribeEmail({ ...filterData })

    await revalidateModules([RequestModuleEnum.Contacts])

    toast.success(t('dashboard.contacts.actions.email_unsubscribed'))
    closeMenu()
  }

  const onDeleteContacts = async () => {
    await deleteContacts({ ...filterData })

    await revalidateModules([RequestModuleEnum.Contacts], true)

    toast.success(t('dashboard.contacts.actions.contacts_removed_toast'))
    closeMenu()
  }

  const [campaignId, setCampaignId] = useState<number | undefined>(undefined)
  const { tags } = useTagsAll(tagAddModalOpened || tagRemoveModalOpened)

  const { campaignsList } = useCampaignsList()
  const { campaignSteps, isStepsValidating } = useContactCampaigns({
    shouldFetch: campaignSubscribeModalOpened || campaignUnsubscribeModalOpened,
    id: campaignId,
  })

  useEffect(() => {
    if (tags) {
      setTagData(tags.items.map(el => ({ id: el.id, caption: el.name })))
    }
  }, [tags])

  useEffect(() => {
    if (campaignsList) {
      setCampaignData(campaignsList.items.map(el => ({ id: el.id, caption: el.name })))
    }
  }, [campaignsList])

  useEffect(() => {
    if (campaignSteps) {
      setCampaignStepData(
        campaignSteps.items.map(el => ({ id: el.id, caption: el.mailing.currentEmail.subject })),
      )
    }
  }, [campaignSteps])

  const shouldRenderExportButton =
    !isWsAssistant &&
    (isSupportImpersonation
      ? checkImpersonatorAdminRoles([AdminRolesEnum.admin, AdminRolesEnum.superAdmin])
      : true)

  const onExport = async (data: ExportOptionsInterface<string>) => {
    const utmParameters = data.exportColumns.filter(el => el in UtmParameterEnum)
    const contactFields = data.exportColumns.filter(
      el =>
        !(el in UtmParameterEnum) &&
        el !== ExtraColumnDataItemsIdsEnum.registered_at &&
        el !== ExtraColumnDataItemsIdsEnum.tags,
    )
    const includeTags = data.exportColumns.some(el => el === ExtraColumnDataItemsIdsEnum.tags)
    const includeRegisteredAt = data.exportColumns.some(
      el => el === ExtraColumnDataItemsIdsEnum.registered_at,
    )

    const extraColumns: ContactExportInterface['exportOptions']['extraColumns'] = []
    if (includeTags) {
      extraColumns.push(ExtraColumnDataItemsIdsEnum.tags)
    }
    if (includeRegisteredAt) {
      extraColumns.push(ExtraColumnDataItemsIdsEnum.registered_at)
    }

    try {
      await exportContacts(
        {
          ...filterData,
          exportOptions: {
            delimiter: data.delimiter,
            utmParameters: utmParameters,
            contactFields: contactFields,
            extraColumns: extraColumns.length ? extraColumns : undefined,
          },
        },
        postFetcher,
      )
      toast.success(t('dashboard.contacts.export_success'))
      setExportModalOpened(false)
      closeMenu()
    } catch (e) {
      throw e
    }
  }

  const onModalClose = () => setErrors(defaultErrors)

  const allContactsSelects = selectedAll && exceptionIds.size === 0 && conditions.length === 0

  return (
    <>
      {renderListingSelect(
        <div className="flex justify-center gap-5">
          <DangerButton onClick={() => setDeleteModalOpened(true)}>
            {t('global.delete')}
          </DangerButton>
          <ActionMenu
            menuItemsClassName="z-30"
            menuItems={[
              <ActionMenuItem
                key="add-tag"
                label={t('dashboard.contacts.actions.add_tag')}
                onClick={() => setTagAddModalOpened(true)}
              />,
              <ActionMenuItem
                key="remove-tag"
                label={t('dashboard.contacts.actions.remove_tag')}
                onClick={() => setTagRemoveModalOpened(true)}
              />,
              <ActionMenuItem
                key="subscribe-to-campaign"
                label={t('dashboard.contacts.actions.subscribe_to_campaign')}
                onClick={() => setCampaignSubscribeModalOpened(true)}
              />,
              <ActionMenuItem
                key="unsubscribe-from-campaign"
                label={t('dashboard.contacts.actions.unsubscribe_from_campaign')}
                onClick={() => setCampaignUnsubscribeModalOpened(true)}
              />,
              <ActionMenuItem
                key="resubscribe-from-marketing-email"
                label={t('dashboard.contacts.actions.resubscribe_from_marketing_email')}
                onClick={() => resubscribeEmailModal.open()}
              />,
              <ActionMenuItem
                key="unsubscribe-from-marketing-email"
                label={t('dashboard.contacts.actions.unsubscribe_from_marketing_email')}
                onClick={() => setEmailUnsubscribeModalOpened(true)}
              />,
              shouldRenderExportButton && (
                <ActionMenuItem
                  key="export"
                  label={t('global.export')}
                  onClick={() => setExportModalOpened(true)}
                />
              ),
            ]}
            className={'relative'}
          >
            {({ open }) => (
              <div
                className={twMerge(
                  'ring-brand-primary flex items-center justify-center rounded-lg border px-4 py-2.5 outline-hidden ring-offset-1 focus-visible:ring-2',
                  open ? 'border-brand-primary' : 'border-text-secondary/30',
                )}
              >
                <div className={'flex items-center gap-3'}>
                  {t('global.more_actions')}
                  <ArrowDownIcon
                    className={twMerge(
                      'transition-transform duration-300',
                      open ? 'rotate-0' : 'rotate-180',
                    )}
                    aria-hidden="true"
                  />
                </div>
              </div>
            )}
          </ActionMenu>
        </div>,
      )}
      <CsvExportModal
        className={'!mb-32'}
        title={t('dashboard.contacts.export_contacts')}
        opened={exportModalOpened}
        onClose={() => setExportModalOpened(false)}
        columns={getExportColumnsData({ t, fields, needShowUtmParameters })}
        handleExportCsv={onExport}
      />
      <CreateModal
        afterLeave={onModalClose}
        modalClassName={`my-auto`}
        fullCaption={t('dashboard.contacts.actions.add_tag_to_contacts')}
        buttonCaption={t('dashboard.contacts.actions.add_tag_to_contacts')}
        needToast={false}
        opened={tagAddModalOpened}
        onClose={() => setTagAddModalOpened(false)}
        defaultValues={defaultTagValues}
        creatable={[
          {
            field: 'tag',
            onRender: (value, onChange) => (
              <FormSelect
                error={errors.tag}
                label={t('dashboard.contacts.choose_tag')}
                data={tagData}
                value={value}
                onChange={data => onChange(data)}
                key={'contact-add-tag'}
              />
            ),
          },
        ]}
        onCreate={onAddTag}
        requiredFields={['tag']}
      />
      <CreateModal
        afterLeave={onModalClose}
        modalClassName={`my-auto`}
        fullCaption={t('dashboard.contacts.actions.remove_tag_from_contacts')}
        buttonCaption={t('dashboard.contacts.actions.remove_tag_from_contacts')}
        needToast={false}
        opened={tagRemoveModalOpened}
        onClose={() => setTagRemoveModalOpened(false)}
        defaultValues={defaultTagValues}
        creatable={[
          {
            field: 'tag',
            onRender: (value, onChange) => (
              <FormSelect
                error={errors.tag}
                label={t('dashboard.contacts.choose_tag')}
                data={tagData}
                value={value}
                onChange={data => onChange(data)}
                key={'contact-remove-tag'}
              />
            ),
          },
        ]}
        onCreate={onRemoveTag}
        requiredFields={['tag']}
      />
      <CreateModal
        modalClassName={`my-auto`}
        afterLeave={() => {
          onModalClose()
          setCampaignId(undefined)
        }}
        fullCaption={t('dashboard.contacts.actions.subscribe_contacts_to_campaign')}
        buttonCaption={t('global.subscribe')}
        needToast={false}
        opened={campaignSubscribeModalOpened}
        onClose={() => setCampaignSubscribeModalOpened(false)}
        defaultValues={defaultCampaignValues}
        creatable={[
          {
            field: 'campaign',
            onRender: (value, _, state, onChangeState) => (
              <FormSelect
                required
                error={errors.campaign}
                label={t('dashboard.contacts.campaign')}
                data={campaignData}
                placeholder={t('dashboard.contacts.actions.select_campaign')}
                value={value}
                onChange={data => {
                  setCampaignId(data)
                  onChangeState({ ...state, campaign: data, campaignStep: undefined })
                }}
                key={'contact-subscribe-to-campaign'}
              />
            ),
          },
          {
            field: 'campaignStep',
            onRender: (value, onChange) => (
              <FormSelect
                error={errors.campaignStep}
                label={
                  <Hint label={t('dashboard.contacts.actions.campaign_step_description')}>
                    {t('dashboard.contacts.actions.starting_campaign_step')}
                  </Hint>
                }
                data={campaignStepData}
                disabled={!campaignSteps}
                isPreFetching={isStepsValidating}
                placeholder={t('dashboard.contacts.actions.beginning_of_campaign')}
                value={value}
                onChange={data => onChange(data)}
                key={'contact-subscribe-to-campaign-step'}
              />
            ),
          },
        ]}
        onCreate={onSubscribeToCampaign}
        requiredFields={['campaign']}
      />
      <CreateModal
        modalClassName={`my-auto`}
        afterLeave={onModalClose}
        fullCaption={t('dashboard.contacts.actions.unsubscribe_contacts_from_campaign')}
        buttonCaption={t('dashboard.contacts.cancel_subscription')}
        needToast={false}
        opened={campaignUnsubscribeModalOpened}
        onClose={() => setCampaignUnsubscribeModalOpened(false)}
        defaultValues={defaultCampaignValues}
        creatable={[
          {
            field: 'campaign',
            onRender: (value, onChange) => (
              <FormSelect
                error={errors.campaign}
                label={t('dashboard.contacts.campaign')}
                data={campaignData}
                placeholder={t('dashboard.contacts.actions.select_campaign')}
                value={value}
                onChange={data => onChange(data)}
                key={'contact-unsubscribe-from-campaign'}
              />
            ),
          },
        ]}
        onCreate={onUnsubscribeFromCampaign}
        requiredFields={['campaign']}
      />
      <ConfirmationModal
        className={`my-auto`}
        title={t('dashboard.contacts.actions.confirm_resubscribe')}
        confirmationText={t('dashboard.contacts.actions.confirm_resubscribe_description')}
        opened={resubscribeEmailModal.opened}
        onClose={resubscribeEmailModal.close}
        onConfirm={onResubscribeEmail}
      />
      <ConfirmationModal
        className={`my-auto`}
        title={t('dashboard.contacts.actions.confirm_unsubscribe')}
        confirmationText={t('dashboard.contacts.actions.confirm_unsubscribe_description')}
        opened={emailUnsubscribeModalOpened}
        onClose={() => setEmailUnsubscribeModalOpened(false)}
        onConfirm={onUnsubscribeEmail}
      />
      <ConfirmationModal
        className={`my-auto`}
        confirmationText={
          allContactsSelects ? (
            <TransLoco
              t={t}
              i18nKey={'dashboard.contacts.actions.delete_all_contacts_confirm'}
              components={{ b: <span className={'font-bold'} /> }}
            />
          ) : (
            t('dashboard.contacts.actions.delete_contacts_confirm')
          )
        }
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        onConfirm={onDeleteContacts}
      />
    </>
  )
}

export default ContactsMenu
